import { MenuGroup, MenuItem } from './_metronic/layout/components/sidebar/sidebar-menu/types'
import { type MenuTreeNode } from './app/modules/Management/core/_requests'
import { filterPermanentlyHiddenMenuNodes } from './app/shared/navigation/permanentlyHiddenMenus'
import { COASTAL_POSITION_REPORT_ROUTE, DPR_ROUTE } from './app/modules/operations/core/routes'



// 1) helper: always ensure "Home" exists (top of the list)
const ensureHome = (groups: MenuGroup[]): MenuGroup[] => {
  if (!groups.length) return groups
  const [first, ...rest] = groups

  const hasHome = first.menu.some((m) => m.name === 'Home')

  if (hasHome) return groups

  // Prefer route/icon from the static map; fallback to defaults
  const homeMeta =
    STATIC_MODULES['Home'] ?? ({ route: '/dashboard', icon: 'Home', svg_location: 'menu' } as RouteIcon)

  const homeItem: MenuItem = {
    name: 'Home',
    route: homeMeta.route,
    icon: (homeMeta.icon as string) ?? 'Home',
    svg_location: (homeMeta.svg_location as string) ?? 'menu',
    // no submenu
  }

  return [{ ...first, menu: [homeItem, ...first.menu] }, ...rest]
}


/**
 * -------------------------
 * 1) STATIC (existing) DATA
 * -------------------------
 * Keep your existing hardcoded role access and the full master menu tree.
 * We still use these for all roles except operator (roleId === 6),
 * and as a fallback if the API fails.
 */

// Configuration for 3rd level menu filtering (nested submenus)
const roleNestedAccess: {
  [roleId: string]: {
    [menuName: string]: {
      [submenuName: string]: string[] // 3rd level items allowed for this submenu
    }
  }
} = {
  '4': {
    Technical: {
      Procurement: [
        'Overview',
        'Requisitions',
        'RFQ Management',
        'Quotation Management',
        'Purchase Orders',
        'Procurement Trail',
        'Goods Receipt',
        'Invoice Management',
        'Credit Note'
        // Excluding 'Audit Trail' and 'User Management' for role 4
      ]
    },
    QHSE: {
      'Forms & Checklists': [
        'Submissions',
        'Work Permits and Checklists',
        'Office Certificates',
        'Vessel Certificates',
        'Manuals and Plans',
        'Defects List',
        'Near Miss reports',
        'Safety committee meeting',
        'Risk Assessment Library',
      ],
      'Audits and Inspection': [
        'Audit Index',
        'Audit Planning',
        'Audit Findings',
        'Inspection Index',
        'Inspection Planning',
        'Inspection Findings',
      ],
    }
  },
  '8': {
    Technical: {
      Procurement: [
        'Overview',
        'Quotation Management',
        'Purchase Orders',
        'Credit Note',
      ]
    }
  }
}

const roleAccess: {
  [roleId: string]: {
    [menuName: string]: string[] // submenu names allowed for this menu
  }
} = {
  // SuperAdmin
  '1': {
    Home: [],
    Operations: ['Dashboard', 'Voyage Reports', 'Position Reports', 'Position Report New', 'Daily Progress Reports', 'Cargo Operations', 'Library', 'In-port Consumption Report',
      'Off Shore Report',
      'Coastal Position Report',
      'Form Index',
      'Reports',
      'Defects List',
      'Coastal Position Report',],
    // Procurement: ['Requisitions', 'Purchase Orders', 'Vendors', 'Inventory'],
    Admin: ['Companies', 'Sub-Companies', 'Vessels', 'User Management', 'Vendor Management'],
    Crewing: [
      'Crew Master',
      'Crew Assignment',
      'Crew Resthours and Overtime',
      'Crew Overtime',
      'Crew Deduction',
      'Crew Claims and Reimbursement',
      'Crew Allotment and Advance',
      'Crew Appraisal',
      'Crew Training, Test & Verification',
      'Crew Complaints',
    ],
    'Work & Hours': ['Rest Hour Management', 'Crew Overtime'],
    Financials: ['Crew Deduction', 'Crew Claims and Reimbursement', 'Crew Allotment and Advance'],
    Training: ['Crew Training, Test & Verification', 'Crew Appraisal'],
    Technical: ['Machinery', 'PMS', 'Monthly Log', /* 'Procurement', */ /* 'Inventory', */ 'Defects List'],
    QHSE: ['Static Templates', 'Document Library', 'Forms & Checklists', 'Audits and Inspection', 'Vessel Audits', 'Vessel Inspection', 'Month End Library'],
    Accounts: ['Dashboard', 'Sales', 'Purchase', 'Vessel Expenses'],
    // HR: [],
    // Admin: [],
  },
  // Crew Member
  '4': {
    Home: [],
    Operations: ['Dashboard', 'Voyage Reports', 'Position Reports', 'Position Report New', 'Daily Progress Reports', 'Cargo Operations',
      'Off Shore Report',
      'Coastal Position Report',
      'Form Index',
      'Reports',
      'Defects List',
      'Coastal Position Report',],
    Crewing: ['Crew List', 'Crew Onboard', 'Crew Deduction', 'Crew Claims and Reimbursement', 'Crew Allotment and Advance', 'Crew Appraisal'],
    Technical: ['Procurement',
      // 'Inventory',
      'PMS', 'Defects List',],
    QHSE: ['Static Templates', 'Document Library', 'Forms & Checklists', 'Audits and Inspection', 'Vessel Audits', 'Vessel Inspection', 'Month End Library'],
    //   Procurement: [
    //   'Overview',
    //   'Requisitions',
    //   'Approvals',
    //   'RFQ Management',
    //   'Vendor Portal',
    //   'Purchase Orders',
    //   'Procurement Trail',
    //   'Goods Receipt',
    //   'Invoice Matching',
    //   'Audit Trail',
    //   'Inventory',
    //   'User Management'
    // ],
    // Accounts: [],
    // HR: [],
    // Admin: [],
  },
  // Company
  '5': {
    Home: [],
    Admin: ['Companies', 'Sub-Companies', 'Vessels', 'User Management', 'Vendor Management'],
    Operations: ['Dashboard', 'Position Reports', 'Position Report New', 'Library', 'Voyage Reports',
      'Off Shore Report',
      'Coastal Position Report',
      'Form Index',
      'Reports',
      'Defects List',
      'Coastal Position Report',],
    // Procurement: ['Requisitions', 'Purchase Orders', 'Vendors', 'Inventory'],
    Crewing: [
      'Crew Master',
      'Crew Assignment',
      'Crew Resthours and Overtime',
      'Crew Deduction',
      'Crew Claims and Reimbursement',
      'Crew Allotment and Advance',
      'Crew Appraisal',
      'Crew Training, Test & Verification',
      'Crew Complaints',
    ],
    // Crewingtest: [
    //   'Crew Management',
    //   'Crew Resthours and Overtime',
    //   'Crew Wages and Portage Bills',
    //   'Assessment and Training',
    //   'Crew Management',
    //   'Crew Listing',
    //   'Crew Assignment',
    // ],
    Technical: ['Machinery', 'PMS', 'Procurement', /* 'Inventory', */  'Defects List',],
    QHSE: ['Static Templates', 'Document Library', 'Forms & Checklists', 'Audits and Inspection', 'Vessel Audits', 'Vessel Inspection', 'Month End Library'],
    Accounts: ['Dashboard', 'Sales', 'Purchase', 'Vessel Expenses'],
    // HR: [],
    // Admin: [],
  },
  // Sub Company
  '2': {
    Home: [],
    // Operations: ['Library'],
    Admin: ['Vessels'],
    Operations: ['Dashboard', 'Voyage Reports', 'Position Reports', 'Position Report New', 'Daily Progress Reports', 'Cargo Operations', 'Library',
      'Off Shore Report',
      'Coastal Position Report',
      'Form Index',
      'Reports',
      'Defects List',
      'Coastal Position Report',],
    // Procurement: ['Requisitions', 'Purchase Orders', 'Vendors', 'Inventory'],
    Crewing: [
      'Crew Master',
      'Crew Assignment',
      'Crew Resthours and Overtime',
      'Crew Deduction',
      'Crew Claims and Reimbursement',
      'Crew Allotment and Advance',
      'Crew Appraisal',
      'Crew Training, Test & Verification',
      'Crew Complaints',
    ],
    Technical: ['Machinery', 'PMS', /* 'Procurement', */ /* 'Inventory', */ 'Monthly Log', 'Defects List',],
    QHSE: ['Static Templates', 'Document Library', 'Forms & Checklists', 'Audits and Inspection', 'Vessel Audits', 'Vessel Inspection', 'Month End Library'],
    Accounts: ['Dashboard', 'Sales', 'Purchase', 'Vessel Expenses'],
    // HR: [],
    // Admin: [],
  },

  // Company Owner / Shore approver
  '8': {
    Home: [],
    Technical: [/* 'Procurement' */],
    Accounts: ['Dashboard', 'Sales', 'Purchase', 'Vessel Expenses'],
  },
  // Operator (role 6) - kept only as a fallback if API fails
  '6': {
    Home: [],
    //   Admin: ['Sub-Companies', 'Vessels'],
    //   Operations: ['Voyage Reports'],
    //   // Procurement: ['Requisitions', 'Purchase Orders', 'Vendors', 'Inventory'],
    //   Crewing: [
    //     'Overview',
    //     'Crew Assignment',
    //     'Rest Hours',
    //     'Crew Overtime',
    //     'Crew Allotment',
    //     'Crew Advance',
    //     'Crew Claims and Reimbursement',
    //     'Appraisal',
    //     'Crew Training',
    //     'Crew Complaints',
    //     'Crew Test & Verification',
    //   ],
    //   Legacy Crewingtest menu folded into Crewing:
    //     'Crew Master',
    //     'Crew Assignment',
    //     'Crew Resthours and Overtime',
    //     'Crew Deduction',
    //     'Crew Claims and Reimbursement',
    //     'Crew Allotment and Advance',
    //     'Crew Appraisal',
    //     'Crew Training, Test & Verification',
    //     'Crew Complaints',
    //   ],
    // //   Technical: [],
    //  QHSE: ['Document Library','Vessel Certificates'],
    // //   Accounts: [],
    //   // HR: [],
    //   // Admin: [],
  },

};

const allMenus: MenuGroup[] = [
  {
    name: '',
    menu: [
      {
        name: 'Home',
        svg_location: 'menu',
        icon: 'Home',
        route: '/dashboard',
        // submenu: [
        //   {
        //     name: 'General',
        //     svg_location: '',
        //     icon: '',
        //     route: '/dashboard/general',
        //   },
        //   {
        //     name: 'Procurement',
        //     svg_location: '',
        //     icon: '',
        //     route: '/dashboard/procurement',
        //   }
        // ]
      },

      // {
      //   name: 'Procurement',
      //   svg_location: 'menu',
      //   icon: 'Technical',
      //   route: '/procurement',
      //   submenu: [
      //     {
      //       name: 'Requisitions',
      //       svg_location: '',
      //       icon: '',
      //       route: '/procurement/requisition',
      //     },
      //     {
      //       name: 'Purchase Orders',
      //       svg_location: '',
      //       icon: '',
      //       route: '/procurement/purchase-orders',
      //     },
      //     {
      //       name: 'Vendors',
      //       svg_location: '',
      //       icon: '',
      //       route: '/procurement/vendors',
      //     },
      //     {
      //       name: 'Inventory',
      //       svg_location: '',
      //       icon: '',
      //       route: '/procurement/inventory-management',
      //     },
      //   ],
      // },
      {
        name: 'Admin',
        svg_location: 'menu',
        icon: 'admin',
        route: '/manage',
        submenu: [
          {
            name: 'Companies',
            svg_location: '',
            icon: '',
            route: '/manage/companies',
          },
          {
            name: 'Sub-Companies',
            svg_location: '',
            icon: '',
            route: '/manage/subcompanies',
          },
          {
            name: 'Vessels',
            svg_location: '',
            icon: '',
            route: '/manage/vessels',
          },
          {
            name: 'User Management',
            svg_location: '',
            icon: '',
            route: '/manage/usermanagement',
          },
          {
            name: 'Vendor Management',
            svg_location: '',
            icon: '',
            route: '/manage/vendormanagement',
          },
        ],
      },
      {
        name: 'Operations',
        svg_location: 'menu',
        icon: 'Operations',
        route: '/operations',
        submenu: [
          {
            name: 'Dashboard',
            svg_location: '',
            icon: '',
            route: '/operations/dashboard',
          },
          {
            name: 'Voyage Reports',
            svg_location: '',
            icon: '',
            route: '/operations/voyages',
          },
          {
            name: 'Position Reports',
            svg_location: '',
            icon: '',
            route: '/operations/overview',
          },
          // {
          //   name: 'Position Report New',
          //   svg_location: '',
          //   icon: '',
          //   route: '/operations/position-report-new',
          // },
          {
            name: 'Daily Progress Reports',
            svg_location: '',
            icon: '',
            route: DPR_ROUTE,
          },
          {
            name: 'Cargo Operations',
            svg_location: '',
            icon: '',
            route: '/operations/cargo-operations',
          },
          {
            name: 'Library',
            svg_location: '',
            icon: '',
            route: '/operations/library',
          },
          {
            name: 'In-port Consumption Report',
            svg_location: '',
            icon: '',
            route: '/operations/inport-consumption',
          },
          // 🔹 NEW: Off Shore Report
          // {
          //   name: 'Off Shore Report',
          //   svg_location: '',
          //   icon: '',
          //   route: '/operations/offshore-report',
          // },

          {
            name: 'Coastal Position Report',
            svg_location: '',
            icon: '',
            route: COASTAL_POSITION_REPORT_ROUTE,
          },

          // 🔹 NEW: Form Index
          // {
          //   name: 'Form Index',
          //   svg_location: '',
          //   icon: '',
          //   route: '/operations/form-index',
          // },

          // 🔹 NEW: Reports (with sub-sub menus)
          {
            name: 'Reports',
            svg_location: '',
            icon: '',
            route: '/operations/reports',
            submenu: [
              // {
              //   name: 'KPIs',
              //   svg_location: '',
              //   icon: '',
              //   route: '/operations/reports/kpis',
              // },
              // {
              //   name: 'Voyage Abstract',
              //   svg_location: '',
              //   icon: '',
              //   route: '/operations/reports/voyage-abstract',
              // },
              {
                name: 'CII Summary',
                svg_location: '',
                icon: '',
                route: '/operations/reports/cii-summary',
              },
              // {
              //   name: 'Cargo Plan/Log',
              //   svg_location: '',
              //   icon: '',
              //   route: '/operations/reports/cargo-plan-log',
              // },
            ],
          },
          {
            name: 'Defects List',
            svg_location: '',
            icon: '',
            route: '/operations/defects-list',
          },
        ],
      },
      {
        name: 'Crewing',
        svg_location: 'menu',
        icon: 'crewing',
        route: '/crewing',
        submenu: [
          // {
          //   name: 'Overview',
          //   svg_location: '',
          //   icon: '',
          //   route: '/crewing/overview',
          // },
          {
            name: 'Crew Master',
            svg_location: '',
            icon: '',
            route: '/crewingtest/crewinglist',
          },
          {
            name: 'Crew Assignment',
            svg_location: '',
            icon: '',
            route: '/crewing/assignment',
          },
          {
            name: 'Crew Resthours and Overtime',
            svg_location: 'none',
            icon: 'none',
            route: '/crewing/resthours',
            submenu: [
              {
                name: 'Rest Hour Management',
                svg_location: 'none',
                icon: 'none',
                route: '/crewingtest/resthours',
              },
              {
                name: 'Crew Overtime',
                svg_location: '',
                icon: '',
                route: '/crewing/crewovertime',
              },
            ],
          },
          // {
          //   name: 'Crew Overtime',
          //   svg_location: '',
          //   icon: '',
          //   route: '/crewing/crewovertime',
          // },
          {
            name: 'Crew Deduction',
            svg_location: '',
            icon: '',
            route: '/crewing/crewdeductions',
          },
          {
            name: 'Crew Claims and Reimbursement',
            svg_location: '',
            icon: '',
            route: '/crewing/crewclaims',
          },
          {
            name: 'Crew Allotment and Advance',
            svg_location: '',
            icon: '',
            route: '/crewing/crewallotment',
          },
          {
            name: 'Crew Appraisal',
            svg_location: '',
            icon: '',
            route: '/crewing/appraisal',
          },
          {
            name: 'Crew Training, Test & Verification',
            svg_location: '',
            icon: '',
            route: '/crewing/crewtraining',
          },
          {
            name: 'Crew Complaints',
            svg_location: '',
            icon: '',
            route: '/crewing/crewcomplaints',
          },
        ],
      },
      {
        name: 'Technical',
        svg_location: 'menu',
        icon: 'Technical',
        route: '/technical',
        submenu: [
          {
            name: 'Machinery',
            svg_location: '',
            icon: '',
            route: '/technical/machinery',
          },
          // 🔹 NEW: PMS
          {
            name: 'PMS',
            svg_location: '',
            icon: '',
            route: '/technical/pms',
            submenu: [
              {
                name: 'Dashboard',
                svg_location: '',
                icon: '',
                route: '/technical/pms/dashboard',
              },
              {
                name: 'Jobs',
                svg_location: '',
                icon: '',
                route: '/technical/pms/jobs',
              },
              // {
              //   name: 'Plans',
              //   svg_location: '',
              //   icon: '',
              //   route: '/technical/pms/plans',
              // },
              // {
              //   name: 'Templates',
              //   svg_location: '',
              //   icon: '',
              //   route: '/technical/pms/templates',
              // },
              {
                name: 'Running Hours',
                svg_location: '',
                icon: '',
                route: '/technical/pms/running-hours',
              },
              {
                name: 'Calendar Planning',
                svg_location: '',
                icon: '',
                route: '/technical/pms/calendar-planning',
              },
            ],
          },

          // 🔹 NEW: Monthly Log
          {
            name: 'Monthly Log',
            svg_location: '',
            icon: '',
            route: '/technical/monthly-log',
          },
          {
            name: 'Procurement',
            svg_location: '',
            icon: '',
            route: '/technical/procurement',
            submenu: [
              {
                name: 'Overview',
                svg_location: '',
                icon: '',
                route: '/procurement/overview',
              },
              {
                name: 'Requisitions',
                svg_location: '',
                icon: '',
                route: '/procurement/requisition',
              },
              {
                name: 'RFQ Management',
                svg_location: '',
                icon: '',
                route: '/procurement/rfq-management',
              },
              {
                name: 'Quotation Management',
                svg_location: '',
                icon: '',
                route: '/procurement/quotation-management',
              },
              {
                name: 'Purchase Orders',
                svg_location: '',
                icon: '',
                route: '/procurement/purchase-orders',
              },
              {
                name: 'Approval Limits',
                svg_location: '',
                icon: '',
                route: '/procurement/approval-library',
              },
              {
                name: 'Currency Exchange Rates',
                svg_location: '',
                icon: '',
                route: '/procurement/currency-exchange-rates',
              },
              {
                name: 'Company PO Info',
                svg_location: '',
                icon: '',
                route: '/procurement/company-po-info',
              },
              {
                name: 'Procurement Trail',
                svg_location: '',
                icon: '',
                route: '/procurement/delivery-tracking',
              },
              {
                name: 'Goods Receipt',
                svg_location: '',
                icon: '',
                route: '/procurement/goods-receipt',
              },
              {
                name: 'Invoice Management',
                svg_location: '',
                icon: '',
                route: '/procurement/invoice-management',
              },
              {
                name: 'Credit Note',
                svg_location: '',
                icon: '',
                route: '/procurement/credit-note',
              },
              {
                name: 'Audit Trail',
                svg_location: '',
                icon: '',
                route: '/procurement/audit-trail',
              },
              {
                name: 'User Management',
                svg_location: '',
                icon: '',
                route: '/procurement/user-management',
              },
            ]
          },
          // {
          //   name: 'Inventory',
          //   svg_location: '',
          //   icon: '',
          //   route: '/procurement/inventory-management',
          // },
          {
            name: 'Defects List',
            svg_location: '',
            icon: '',
            route: '/technical/defects-list',
          },
        ]
      },
      {
        name: 'QHSE',
        svg_location: 'menu',
        icon: 'qhse',
        route: '/qhse',
        submenu: [

          {
            name: 'Static Templates',
            svg_location: '',
            icon: '',
            route: '/qhse/forms-checklists/library',
          },
          {
            name: 'Document Library',
            svg_location: '',
            icon: '',
            route: '/qhse/document-library',
          },
          {
            name: 'Forms & Checklists',
            svg_location: '',
            icon: '',
            route: '/qhse/forms-checklists',
            submenu: [
              {
                name: 'Submissions',
                svg_location: '',
                icon: '',
                route: '/qhse/forms-checklists/submissions',
              },
              {
                name: 'Work Permits and Checklists',
                svg_location: '',
                icon: '',
                route: '/qhse/work-permits-and-checklists',
              },
              {
                name: 'Office Certificates',
                svg_location: '',
                icon: '',
                route: '/qhse/office-certificates',
              },
              {
                name: 'Vessel Certificates',
                svg_location: '',
                icon: '',
                route: '/qhse/vessel-certificates',
              },
              {
                name: 'Manuals and Plans',
                svg_location: '',
                icon: '',
                route: '/qhse/manuals-and-plans',
              },
              {
                name: 'Defects List',
                svg_location: '',
                icon: '',
                route: '/qhse/defects-list',
              },
              {
                name: 'Near Miss reports',
                svg_location: '',
                icon: '',
                route: '/qhse/accident-near-miss-reports',
              },
              {
                name: 'Safety committee meeting',
                svg_location: '',
                icon: '',
                route: '/qhse/safety-committee-meeting',
              },
              {
                name: 'Risk Assessment Library',
                svg_location: '',
                icon: '',
                route: '/qhse/risk-assessment',
              },
            ],
          },
          {
            name: 'Audits and Inspection',
            svg_location: '',
            icon: '',
            route: '/qhse/audit-index',
            submenu: [
              {
                name: 'Audit Index',
                svg_location: '',
                icon: '',
                route: '/qhse/audit-index',
              },
              {
                name: 'Audit Planning',
                svg_location: '',
                icon: '',
                route: '/qhse/audit-planning',
              },
              {
                name: 'Audit Findings',
                svg_location: '',
                icon: '',
                route: '/qhse/audit-findings',
              },
              {
                name: 'Inspection Index',
                svg_location: '',
                icon: '',
                route: '/qhse/inspindex',
              },
              {
                name: 'Inspection Planning',
                svg_location: '',
                icon: '',
                route: '/qhse/insplanning',
              },
              {
                name: 'Inspection Findings',
                svg_location: '',
                icon: '',
                route: '/qhse/inspection-findings',
              },
            ],
          },
          {
            name: 'Month End Library',
            svg_location: '',
            icon: '',
            route: '/qhse/month-end-reports',
            submenu: [
              {
                name: 'Main Engine Consumption',
                svg_location: 'none',
                icon: 'none',
                route: '/qhse/month-end-reports/main-engine-consumption',
              },
              {
                name: 'Machinery Status',
                svg_location: 'none',
                icon: 'none',
                route: '/qhse/month-end-reports/machinery-status',
              },
              {
                name: 'Months End Report',
                svg_location: 'none',
                icon: 'none',
                route: '/qhse/month-end-reports/months-end-report',
              },
            ],
          },
        ],
      },
      {
        name: 'Accounts',
        svg_location: 'menu',
        icon: 'accounts',
        route: '/accounts',
        submenu: [
          {
            name: 'Dashboard',
            svg_location: 'none',
            icon: 'none',
            route: '/accounts/dashboard',
          },
          {
            name: 'Sales',
            svg_location: 'none',
            icon: 'none',
            route: '/accounts/sales',
            submenu: [
              {
                name: 'Customers',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/sales/customers',
              },
              {
                name: 'Quotes',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/sales/quotes',
              },
              {
                name: 'Sales Orders',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/sales/sales-orders',
              },
              {
                name: 'Invoices',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/sales/invoices',
              },
              {
                name: 'Recurring Invoices',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/sales/recurring-invoices',
              },
              {
                name: 'Delivery Challans',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/sales/delivery-challans',
              },
              {
                name: 'Payments Received',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/sales/payments-received',
              },
              {
                name: 'Credit Notes',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/sales/credit-notes',
              },
            ],
          },
          {
            name: 'Purchase',
            svg_location: 'none',
            icon: 'none',
            route: '/accounts/purchase',
            submenu: [
              {
                name: 'Vendors',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/purchase/vendors',
              },
              {
                name: 'Expenses',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/purchase/expenses',
              },
              {
                name: 'Recurring Expenses',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/purchase/recurring-expenses',
              },
              {
                name: 'Purchase Orders',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/purchase/purchase-orders',
              },
              {
                name: 'Bills',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/purchase/bills',
              },
              {
                name: 'Recurring Bills',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/purchase/recurring-bills',
              },
              {
                name: 'Payments Made',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/purchase/payments-made',
              },
              {
                name: 'Vendor Credits',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/purchase/vendor-credits',
              },
            ],
          },
          {
            name: 'Vessel Expenses',
            svg_location: 'none',
            icon: 'none',
            route: '/accounts/vessel-expenses',
            submenu: [
              {
                name: 'Budgets',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/budget-expense',
              },
              {
                name: 'Invoices',
                svg_location: 'none',
                icon: 'none',
                route: '/accounts/invoices',
              },
            ],
          },
        ],
      },
      // {
      //   name: 'HR',
      //   svg_location: 'menu',
      //   icon: 'hr',
      //   route: '/polls'
      // },
    ],
  },
];


/**
 * 2) UTILITIES TO MAP THE STATIC TREE
 *    We build a route/icon index so we can map API names -> routes/icons.
 */

type RouteIcon = { route: string; icon?: string; svg_location?: string }

const kebab = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

const normalizeKey = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '')

const buildIndexes = (menus: MenuGroup[]) => {
  const moduleMap: Record<string, RouteIcon> = {}
  // leafMap keys we will store:
  // - "Module>>Leaf" (legacy/fallback)
  // - "Module>>Menu>>Leaf" (preferred for sub-sub menus)
  const leafMap: Record<string, RouteIcon> = {}

  const traverse = (moduleName: string, parentName: string | null, item: MenuItem) => {
    // top-level items act as modules
    if (!moduleName) moduleName = item.name

    if (item.submenu?.length) {
      // record module shell if top-level
      if (!leafMap[`${item.name}>>__SELF__`]) {
        moduleMap[item.name] = {
          route: item.route,
          icon: item.icon,
          svg_location: item.svg_location,
        }
      }
      item.submenu.forEach((sub) => traverse(moduleName, item.name, sub))
    } else {
      // leaf (no submenu)
      // store both short and full keys for resilience
      leafMap[`${moduleName}>>${item.name}`] = {
        route: item.route,
        icon: item.icon,
        svg_location: item.svg_location,
      }
      if (parentName) {
        leafMap[`${moduleName}>>${parentName}>>${item.name}`] = {
          route: item.route,
          icon: item.icon,
          svg_location: item.svg_location,
        }
      }
    }
  }

  menus.forEach((group) => {
    group.menu.forEach((item) => {
      moduleMap[item.name] = {
        route: item.route,
        icon: item.icon,
        svg_location: item.svg_location,
      }
      if (item.submenu) item.submenu.forEach((sub) => traverse(item.name, item.name, sub))
    })
  })

  return { moduleMap, leafMap }
}


const { moduleMap: STATIC_MODULES, leafMap: STATIC_LEAVES } = buildIndexes(allMenus)

const buildNormalizedLeafIndex = (leafMap: Record<string, RouteIcon>) => {
  const normalized: Record<string, RouteIcon> = {}

  Object.entries(leafMap).forEach(([key, value]) => {
    const parts = key.split('>>')
    if (parts.length < 2) return
    const normParts = parts.map((p) => normalizeKey(p))
    normalized[normParts.join('>>')] = value
  })

  return normalized
}

const STATIC_LEAVES_NORMALIZED = buildNormalizedLeafIndex(STATIC_LEAVES)

const LEAF_NAME_ALIASES: Record<string, Record<string, string>> = {
  crewing: {
    crewallotment: 'Crew Allotment and Advance',
    crewadvance: 'Crew Allotment and Advance',
    crewallotmentandadvance: 'Crew Allotment and Advance',
    crewclaims: 'Crew Claims and Reimbursement',
    crewclaim: 'Crew Claims and Reimbursement',
    crewclaimsandreimbursement: 'Crew Claims and Reimbursement',
    crewdeduction: 'Crew Deduction',
    crewdeductions: 'Crew Deduction',
    crewtraining: 'Crew Training, Test & Verification',
    crewtestverification: 'Crew Training, Test & Verification',
    testverification: 'Crew Training, Test & Verification',
    crewtrainingtestverification: 'Crew Training, Test & Verification',
    appraisal: 'Crew Appraisal',
    appraisals: 'Crew Appraisal',
    crewappraisal: 'Crew Appraisal',
  },
  operations: {
    cargoplanlog: 'Chief Engineer Log',
    chiefengineerlog: 'Chief Engineer Log',
    coastalpositionreports: 'Coastal Position Report',
    dailypositionreport: 'Daily Progress Reports',
    dailypositionreports: 'Daily Progress Reports',
    dailymovementreport: 'Daily Progress Reports',
    dailymovementreports: 'Daily Progress Reports',
  },
}

const resolveCanonicalLeafName = (moduleName: string, leafName: string): string => {
  const normModule = normalizeKey(moduleName)
  const normLeaf = normalizeKey(leafName)
  const aliasesForModule = LEAF_NAME_ALIASES[normModule]
  if (aliasesForModule) {
    return aliasesForModule[normLeaf] ?? leafName
  }
  return leafName
}

const shouldHideProcurementApprovalLeaf = (
  moduleName: string,
  leafName: string,
  parentName?: string
): boolean => {
  const normalizedLeaf = normalizeKey(leafName)
  if (normalizedLeaf !== 'approvals') return false

  const normalizedModule = normalizeKey(moduleName)
  const normalizedParent = normalizeKey(parentName || '')
  return normalizedModule === 'procurement' || normalizedParent === 'procurement'
}

const dedupeMenuItemsByName = (items: MenuItem[]): MenuItem[] => {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = normalizeKey(item.name)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const CREW_FLOW_ORDER_KEYS = [
  normalizeKey('Crew Deduction'),
  normalizeKey('Crew Claims and Reimbursement'),
  normalizeKey('Crew Allotment and Advance'),
]

const reorderCrewingFinancialFlow = (moduleName: string, items: MenuItem[]): MenuItem[] => {
  if (normalizeKey(moduleName) !== 'crewing') return items

  const flowSet = new Set(CREW_FLOW_ORDER_KEYS)
  const keyedItems = items.map((item) => ({
    item,
    key: normalizeKey(resolveCanonicalLeafName(moduleName, item.name)),
  }))

  const presentByKey = new Map<string, MenuItem>()
  keyedItems.forEach(({ item, key }) => {
    if (flowSet.has(key) && !presentByKey.has(key)) {
      presentByKey.set(key, item)
    }
  })

  const orderedPresent = CREW_FLOW_ORDER_KEYS
    .map((key) => presentByKey.get(key))
    .filter((item): item is MenuItem => Boolean(item))

  if (orderedPresent.length <= 1) return items

  let idx = 0
  return keyedItems.map(({ item, key }) => {
    if (!flowSet.has(key)) return item
    const replacement = orderedPresent[idx]
    idx += 1
    return replacement ?? item
  })
}

const resolveStaticLeafMeta = (
  moduleName: string,
  leafName: string,
  parentName?: string,
  visited: Set<string> = new Set(),
): RouteIcon | undefined => {
  const visitKey = `${moduleName}>>${parentName ?? ''}>>${leafName}`
  if (visited.has(visitKey)) {
    return undefined
  }
  visited.add(visitKey)
  // 1) exact label match
  if (parentName) {
    const exactNested = STATIC_LEAVES[`${moduleName}>>${parentName}>>${leafName}`]
    if (exactNested) return exactNested
  }
  const exact = STATIC_LEAVES[`${moduleName}>>${leafName}`]
  if (exact) return exact

  // 2) normalized label match (case/punctuation insensitive)
  const normModule = normalizeKey(moduleName)
  const normLeaf = normalizeKey(leafName)
  const normParent = parentName ? normalizeKey(parentName) : ''

  if (normParent) {
    const normNested = STATIC_LEAVES_NORMALIZED[`${normModule}>>${normParent}>>${normLeaf}`]
    if (normNested) return normNested
  }

  const normDirect = STATIC_LEAVES_NORMALIZED[`${normModule}>>${normLeaf}`]
  if (normDirect) return normDirect

  // 3) alias-based match for known backend/frontend naming drifts
  const aliasesForModule = LEAF_NAME_ALIASES[normModule]
  if (aliasesForModule) {
    const aliasCanonical = aliasesForModule[normLeaf]
    if (aliasCanonical) {
      return resolveStaticLeafMeta(moduleName, aliasCanonical, parentName, visited)
    }
  }

  return undefined
}

/**
 * 3) API TYPES + MAPPER
 */

// type ApiLevel = 'MODULE' | 'MENU'
// interface ApiMenuNode {
//   level: ApiLevel
//   id: number
//   name: string
//   sortOrder: number | null
//   canView: boolean
//   canAdd: boolean
//   canEdit: boolean
//   canDelete: boolean
//   children: ApiMenuNode[]
// }
type ApiMenuNode = MenuTreeNode

const MACHINERY_PERMISSION_GROUPING_SUBMENUS = new Set([
  'Equipment',
  'Subcomponent',
  'Component',
  'Part',
])

const shouldHideMachineryPermissionGroupingSubmenu = (moduleName: string, submenuName: string): boolean =>
  moduleName === 'Technical' && MACHINERY_PERMISSION_GROUPING_SUBMENUS.has(submenuName)

const mapApiTreeToMenuGroups = (apiRoot: ApiMenuNode | ApiMenuNode[]): MenuGroup[] => {
  const modules: ApiMenuNode[] = Array.isArray(apiRoot) ? apiRoot : [apiRoot]

  const group: MenuGroup = { name: '', menu: [] }

  modules
    .filter((m) => m.level === 'MODULE')
    .forEach((mod) => {
      // find module skeleton from static map (route + icon)
      const modMeta =
        STATIC_MODULES[mod.name] ??
        ({
          route: `/${kebab(mod.name)}`,
          icon: 'none',
          svg_location: 'menu',
        } as RouteIcon)

      const moduleItem: MenuItem = {
        name: mod.name,
        route: modMeta.route,
        icon: (modMeta.icon as string) ?? 'none',
        svg_location: (modMeta.svg_location as string) ?? 'menu',
        submenu: [],
      }

      // children -> only show items with canView === true
      const visibleChildren = (mod.children || []).filter((c: MenuTreeNode) => c.level === 'MENU' && c.canView)

      visibleChildren.forEach((child: MenuTreeNode) => {
        const canonicalChildName = resolveCanonicalLeafName(mod.name, child.name)
        if (shouldHideProcurementApprovalLeaf(mod.name, canonicalChildName)) {
          return
        }
        const leafMeta =
          resolveStaticLeafMeta(mod.name, canonicalChildName) ??
          resolveStaticLeafMeta(mod.name, child.name) ??
          ({
            route: `/${kebab(mod.name)}/${kebab(canonicalChildName)}`,
            icon: 'none',
            svg_location: '',
          } as RouteIcon)

        const childItem: MenuItem = {
          name: canonicalChildName,
          route: leafMeta.route,
          icon: (leafMeta.icon as string) ?? 'none',
          svg_location: (leafMeta.svg_location as string) ?? '',
        }

        // if backend later returns nested children for a leaf, map them too
        if (child.children?.length) {
          const childChildren: MenuTreeNode[] = child.children
          const subLeaves: MenuItem[] = childChildren
            .filter((cc: MenuTreeNode) => {
              if (!cc.canView) return false
              const canonicalNestedName = resolveCanonicalLeafName(mod.name, cc.name)
              if (shouldHideMachineryPermissionGroupingSubmenu(mod.name, canonicalNestedName)) {
                return false
              }
              return !shouldHideProcurementApprovalLeaf(mod.name, canonicalNestedName, childItem.name)
            })
            .map((cc: MenuTreeNode) => {
              const canonicalNestedName = resolveCanonicalLeafName(mod.name, cc.name)
              const nestedMeta =
                // prefer full path; fall back to short key; then fallback to generated route
                resolveStaticLeafMeta(mod.name, canonicalNestedName, childItem.name) ??
                resolveStaticLeafMeta(mod.name, cc.name, child.name) ??
                ({
                  route: `/${kebab(mod.name)}/${kebab(childItem.name)}/${kebab(canonicalNestedName)}`,
                  icon: 'none',
                  svg_location: '',
                } as RouteIcon)


              return {
                name: canonicalNestedName,
                route: nestedMeta.route,
                icon: (nestedMeta.icon as string) ?? 'none',
                svg_location: (nestedMeta.svg_location as string) ?? '',
              }
            })

          if (subLeaves.length) {
            childItem.submenu = dedupeMenuItemsByName(subLeaves)
          }
        }

        ; (moduleItem.submenu as MenuItem[]).push(childItem)
      })

      moduleItem.submenu = reorderCrewingFinancialFlow(
        moduleItem.name,
        dedupeMenuItemsByName(moduleItem.submenu || [])
      )

      if (mod.name.toLowerCase() === 'operations') {
        const hasCoastal = moduleItem.submenu?.some((item) => item.name === 'Coastal Position Report')
        if (!hasCoastal) {
          moduleItem.submenu?.push({
            name: 'Coastal Position Report',
            route: '/operations/coastal-position-report',
            icon: 'none',
            svg_location: '',
          })
        }
      }

      // Show the module only if it has at least one visible child
      if ((moduleItem.submenu?.length || 0) > 0) {
        group.menu.push(moduleItem)
      }
    })

  return ensureHome([group])
}

// Build sidebar menu groups directly from a backend-provided tree
export const buildMenusFromTree = (apiRoot: ApiMenuNode | ApiMenuNode[]): MenuGroup[] => {
  return mapApiTreeToMenuGroups(apiRoot)
}


/**
 * 4) PUBLIC APIS
 *
 * - getMenusByRoleSync: original behavior (used for all roles except operator,
 *   and as a fallback in case dynamic fetch fails).
 * - getMenusByRoleAsync: for roleId 6 fetches dynamic menu, otherwise returns static.
 */

const cloneMenuItem = (item: MenuItem): MenuItem => ({
  ...item,
  submenu: item.submenu?.map(cloneMenuItem),
})

const cloneMenuGroups = (groups: MenuGroup[]): MenuGroup[] =>
  groups.map((group) => ({
    ...group,
    menu: group.menu.map(cloneMenuItem),
  }))

/**
 * Complete static sidebar tree for permission-based filtering.
 * Does not apply roleAccess / roleNestedAccess — only optional crew rank presentation.
 */
export const getSidebarStaticMenus = (roleId?: number, rankId?: number): MenuGroup[] => {
  void roleId
  void rankId
  const menus = cloneMenuGroups(allMenus)
  return menus.map((group) => ({
    ...group,
    menu: filterPermanentlyHiddenMenuNodes(group.menu || []),
  }))
}

export const getMenusByRoleSync = (roleId: number, rankId?: number): MenuGroup[] => {
  void rankId
  const allowed = roleAccess[String(roleId)]
  const nestedAllowed = roleNestedAccess[String(roleId)]
  if (!allowed) return []

  return allMenus.map((group) => {
    const filteredMenuItems = group.menu
      // keep only modules/menus explicitly listed for this role
      .filter((item) => Object.prototype.hasOwnProperty.call(allowed, item.name))
      .map((item) => {
        let allowedSubmenus = allowed[item.name] || []

        // keep only allowed 2nd-level submenus
        const filteredSubmenu = item.submenu
          ? item.submenu
            .filter((sub) => allowedSubmenus.includes(sub.name))
            .map((sub) => {
              // Check if we have nested filtering rules for this role and menu combination
              if (nestedAllowed && nestedAllowed[item.name] && nestedAllowed[item.name][sub.name]) {
                const allowedNestedItems = nestedAllowed[item.name][sub.name]
                // Filter 3rd-level items based on the nested access rules
                if (sub.submenu?.length) {
                  const filteredNestedSubmenu = sub.submenu.filter((nestedSub) =>
                    allowedNestedItems.includes(nestedSub.name)
                  )
                  return { ...sub, submenu: filteredNestedSubmenu }
                }
                return sub
              } else {
                // Original behavior: If a 2nd-level submenu is allowed, include ALL its 3rd-level children as-is.
                if (sub.submenu?.length) {
                  return { ...sub, submenu: [...sub.submenu] }
                }
                return sub
              }
            })
          : undefined

        const orderedSubmenu = filteredSubmenu
          ? reorderCrewingFinancialFlow(item.name, filteredSubmenu)
          : undefined

        return {
          ...item,
          submenu: orderedSubmenu,
        }
      })

    return {
      ...group,
      menu: filteredMenuItems,
    }
  })
}


// small local cache to avoid flicker after login
let operatorMenuCache: MenuGroup[] | null = null

export const getMenusByRoleAsync = async (
  roleId: number,
  opts?: { token?: string }
): Promise<MenuGroup[]> => {
  void opts
  if (roleId !== 6) {
    return getMenusByRoleSync(roleId)
  }

  if (operatorMenuCache) return operatorMenuCache

  operatorMenuCache = getMenusByRoleSync(6)
  return operatorMenuCache
}



// export const getMenusByRole = (roleId: number): MenuGroup[] => {
//   const allowed = roleAccess[roleId]
//   if (!allowed) return []

//   return allMenus.map((group) => {
//     const filteredMenuItems = group.menu
//       .filter((item) => allowed.hasOwnProperty(item.name))
//       .map((item) => {
//         const allowedSubmenus = allowed[item.name]
//         const filteredSubmenu = item.submenu
//           ? item.submenu.filter((sub) => allowedSubmenus.includes(sub.name))
//           : undefined

//         return {
//           ...item,
//           submenu: filteredSubmenu,
//         }
//       })

//     return {
//       ...group,
//       menu: filteredMenuItems,
//     }
//   })
// }

// Back-compat: older code imports getMenusByRole from this module
export const getMenusByRole = getMenusByRoleSync
